import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Folder, FolderOpen, Upload, X, Check } from "lucide-react";
import DriveSidebar from "./DriveSidebar";
import DriveToolbar from "./DriveToolbar";
import DriveGridView from "./DriveGridView";
import DriveListView from "./DriveListView";
import DriveAgent from "./DriveAgent";
import { pathSegments } from "./driveUtils";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function DatabasePage() {
  const [path, setPath] = useState("/");
  const [items, setItems] = useState([]);
  const [rootFolders, setRootFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [viewMode, setViewMode] = useState("grid");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newFolder, setNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [menuOpen, setMenuOpen] = useState(null);
  const [agentOpen, setAgentOpen] = useState(false);
  const newFolderRef = useRef(null);

  const load = useCallback(async (p) => {
    setLoading(true);
    setSelected(new Set());
    try {
      const res = await axios.get(`${API}/storage/list?path=${encodeURIComponent(p)}`);
      setItems(res.data.items);
      if (p === "/") setRootFolders(res.data.items.filter(i => i.type === "folder"));
    } finally { setLoading(false); }
  }, []);

  const loadRootFolders = useCallback(async () => {
    const res = await axios.get(`${API}/storage/list?path=/`);
    setRootFolders(res.data.items.filter(i => i.type === "folder"));
  }, []);

  useEffect(() => { load(path); }, [path, load]);
  useEffect(() => { const h = () => setMenuOpen(null); document.addEventListener("click", h); return () => document.removeEventListener("click", h); }, []);
  useEffect(() => { if (newFolder) newFolderRef.current?.focus(); }, [newFolder]);

  const navigate = (p) => { setPath(p); setMenuOpen(null); setNewFolder(false); };
  const openItem = (item) => { if (item.type === "folder") navigate(item.path); };

  const doUpload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("path", path); fd.append("file", f);
        await axios.post(`${API}/storage/upload`, fd);
      }
      await load(path);
    } finally { setUploading(false); }
  };

  const doCreateFolder = async () => {
    if (!newFolderName.trim()) { setNewFolder(false); return; }
    const fd = new FormData(); fd.append("path", path); fd.append("name", newFolderName.trim());
    await axios.post(`${API}/storage/folder`, fd);
    setNewFolderName(""); setNewFolder(false);
    await load(path);
    const res = await axios.get(`${API}/storage/list?path=/`);
    setRootFolders(res.data.items.filter(i => i.type === "folder"));
  };

  const doDelete = async (itemPath) => {
    if (!window.confirm("Delete this item?")) return;
    await axios.delete(`${API}/storage/delete?path=${encodeURIComponent(itemPath)}`);
    await load(path);
  };

  const doDeleteSelected = async () => {
    if (!selected.size || !window.confirm(`Delete ${selected.size} item(s)?`)) return;
    for (const p of selected) await axios.delete(`${API}/storage/delete?path=${encodeURIComponent(p)}`);
    await load(path);
  };

  const doDownload = (itemPath) => window.open(`${API}/storage/download?path=${encodeURIComponent(itemPath)}`, "_blank");

  const startRename = (item) => { setRenaming(item.path); setRenameValue(item.name); setMenuOpen(null); };
  const submitRename = async (item) => {
    if (renameValue.trim() && renameValue !== item.name) {
      const fd = new FormData(); fd.append("path", item.path); fd.append("new_name", renameValue.trim());
      await axios.post(`${API}/storage/rename`, fd);
      await load(path);
    }
    setRenaming(null);
  };

  const toggleSelect = (e, p) => {
    e.stopPropagation();
    setSelected(prev => { const s = new Set(prev); s.has(p) ? s.delete(p) : s.add(p); return s; });
  };

  const viewProps = {
    items, selected, renaming, renameValue, setRenameValue, menuOpen, setMenuOpen,
    onOpen: openItem, onToggleSelect: toggleSelect, onDownload: doDownload,
    onDelete: doDelete, onStartRename: startRename, onSubmitRename: submitRename,
  };

  return (
    <div className="flex h-full bg-background" data-testid="database-page">
      <DriveSidebar rootFolders={rootFolders} currentPath={path} itemCount={items.length} onNavigate={navigate} />

      <div className={`relative flex-1 flex flex-col min-w-0 ${dragging ? "bg-[#F5F4EE]" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
        onDrop={(e) => { e.preventDefault(); setDragging(false); doUpload([...e.dataTransfer.files]); }}>

        <DriveToolbar
          segs={pathSegments(path)} selected={selected} uploading={uploading} viewMode={viewMode}
          agentOpen={agentOpen}
          onNavigate={navigate} onRefresh={() => load(path)} onNewFolder={() => setNewFolder(true)}
          onDeleteSelected={doDeleteSelected} onUpload={doUpload} onViewChange={setViewMode}
          onToggleAgent={() => setAgentOpen(o => !o)}
        />

        {dragging && (
          <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
            <div className="bg-[#F5F4EE] border-2 border-dashed border-[#A8A67A] px-10 py-8 text-center">
              <Upload className="w-8 h-8 text-[#A8A67A] mx-auto mb-2" />
              <p className="font-semibold text-[#A8A67A]">Drop files to upload</p>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="flex gap-2 dot-pulse"><span /><span /><span /></div>
            </div>
          ) : (
            <div>
              {newFolder && (
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <Folder className="w-5 h-5 text-amber-500" />
                  </div>
                  <input ref={newFolderRef} value={newFolderName}
                    onChange={e => setNewFolderName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") doCreateFolder(); if (e.key === "Escape") setNewFolder(false); }}
                    placeholder="Folder name"
                    className="text-sm border border-[#A8A67A] px-2 py-1 focus:outline-none w-40"
                    data-testid="new-folder-input" />
                  <button onClick={doCreateFolder} className="text-[#A8A67A] hover:text-[#C4C29A]"><Check className="w-4 h-4" /></button>
                  <button onClick={() => { setNewFolder(false); setNewFolderName(""); }} className="text-muted-foreground hover:text-red-500"><X className="w-4 h-4" /></button>
                </div>
              )}
              {items.length === 0 && !newFolder ? (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <div className="w-14 h-14 bg-[#F5F4EE] flex items-center justify-center mb-3">
                    <FolderOpen className="w-7 h-7 text-[#D4D2B0]" strokeWidth={1.5} />
                  </div>
                  <p className="font-semibold text-sm mb-1">This folder is empty</p>
                  <p className="text-xs text-muted-foreground">Upload files or create a new folder</p>
                </div>
              ) : viewMode === "grid" ? (
                <DriveGridView {...viewProps} />
              ) : (
                <DriveListView {...viewProps} />
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-2 border-t border-border bg-[#F8F9FA] flex-shrink-0">
          <p className="text-[10px] text-muted-foreground">
            Drag &amp; drop to upload · Double-click folders to open · Use ⋯ for more actions · Click <strong>Agent</strong> for AI file assistant
          </p>
        </div>
      </div>

      {agentOpen && (
        <DriveAgent
          onRefresh={(refreshRoot) => { load(path); if (refreshRoot) loadRootFolders(); }}
          onNavigate={(p) => { navigate(p); }}
        />
      )}
    </div>
  );
}
